/**
 * Daily share-format builder.
 *
 * Pure function (no DOM, no clipboard) so it can be unit-tested. The scene
 * calls buildShareText() with whatever it knows after a daily run, and gets
 * back a string ready to copy.
 *
 * Format (one row, 20 coconuts):
 *
 *   Splat Chimp · 2026-05-27
 *   🥥🥥🥥🥥⭐🥥🥥⬛⬛🥥🥥🥥⭐🥥🥥🥥🥥⬛🥥🥥
 *   17/20 · 1850 pts · 0:48 · 🔥3
 *
 * The grid uses three glyphs:
 *   🥥 - collected, visible coconut
 *   ⭐ - collected, invisible (hidden) coconut
 *   ⬛ - uncollected (any kind)
 *
 * Coconut order in the grid matches the playfield order so two players who
 * collected the same coconuts produce the same grid. The grid is broken into
 * 10-wide rows for legibility if the total exceeds 10.
 */

import { formatTime } from '../game/Score';

export interface CoconutShareEntry {
  invisible: boolean;
  collected: boolean;
}

export interface ShareData {
  /** Daily date key (YYYY-MM-DD). */
  dateKey: string;
  coconuts: CoconutShareEntry[];
  totalCoconuts: number;
  collectedCount: number;
  /** Final score for this attempt. */
  score: number;
  /** Run duration in ms. */
  elapsedMs: number;
  /** Current streak after this run. 0 means no streak. */
  streak: number;
  /** True if the run completed all coconuts. */
  complete: boolean;
}

const GRID_WIDTH = 10;

export function buildShareText(data: ShareData): string {
  const header = `Splat Chimp · ${data.dateKey}`;

  // Grid: one glyph per coconut. Broken into 10-wide rows.
  const glyphs = data.coconuts.map((c) => {
    if (!c.collected) return '⬛';
    return c.invisible ? '⭐' : '🥥';
  });
  const rows: string[] = [];
  for (let i = 0; i < glyphs.length; i += GRID_WIDTH) {
    rows.push(glyphs.slice(i, i + GRID_WIDTH).join(''));
  }
  const grid = rows.join('\n');

  // Status line: score, time, streak, completion indicator.
  const parts: string[] = [];
  parts.push(`${data.collectedCount}/${data.totalCoconuts}`);
  parts.push(`${data.score} pts`);
  parts.push(formatTime(data.elapsedMs));
  if (data.complete) parts.push('✓');
  if (data.streak > 0) parts.push(`🔥${data.streak}`);

  return `${header}\n${grid}\n${parts.join(' · ')}`;
}
